import { plainToClass } from 'class-transformer';
import { ICollectionResponse, ISearchRequestObject } from '@logz-build/typescript';
import { ApiTokenModel } from '@logz-pkg/models';
import { ApiTokenScope } from '@logz-pkg/enums';
import { ICrudApiService } from '../utilities/types';
import { CrudApiService } from '../utilities/crud.api.service';
import { cacheProvider } from '../../core/cache/cache.provider';
import { sortByLastUsed } from '../utilities/utilities';

const searchManipulator = (
  apiTokens: ApiTokenModel[],
  requestObject: ISearchRequestObject,
): ICollectionResponse<ApiTokenModel> => {
  let startPage = 0;
  let endPage = apiTokens.length;

  if (requestObject.pagination) {
    startPage = (requestObject.pagination.pageNumber - 1) * requestObject.pagination.pageSize;
    endPage = requestObject.pagination.pageNumber * requestObject.pagination.pageSize;
  }

  return {
    total: apiTokens.length,
    results: sortByLastUsed<ApiTokenModel>(apiTokens).slice(startPage, endPage),
    pagination: {
      pageNumber: requestObject?.pagination?.pageNumber ?? startPage,
      pageSize: requestObject?.pagination?.pageSize ?? endPage,
    },
  };
};

class ApiTokenApiService implements ICrudApiService<ApiTokenModel> {
  crudApiService = new CrudApiService({ modelDefinition: ApiTokenModel });

  urls = {
    collection: 'api-tokens',
    search: 'api-tokens',
    resource: id => `/api-tokens/${id}`,
  };

  search = async (
    requestObject: ISearchRequestObject,
    filterFn?: (token: ApiTokenModel) => boolean,
  ): Promise<ICollectionResponse<ApiTokenModel>> => {
    // Ensure that populate will fetch fresh data, Token Filters are cached to reduce the amount of requests made on load
    cacheProvider.clear(cacheProvider.CacheKeys.SHARED_TOKENS_FILTERS);

    const searchResult = await this.crudApiService.search(this.urls.search, requestObject, { httpMethod: 'get' });
    const results = searchResult?.results ?? [];

    return searchManipulator(filterFn ? results.filter(filterFn) : results, requestObject);
  };

  create = async (model: ApiTokenModel): Promise<ApiTokenModel> =>
    this.crudApiService.create(this.urls.collection, model);

  updateRole = async ({ id, role }: { id: ApiTokenModel['id']; role: ApiTokenScope }): Promise<ApiTokenModel> => {
    const { data }: { data: ApiTokenModel } = await this.crudApiService.httpService.put(this.urls.resource(id), {
      role,
    });

    // Role-only PUT bypasses crudApiService.update, so hydrate the raw response here to honour the return type.
    return plainToClass(ApiTokenModel, data);
  };

  delete = async (id: ApiTokenModel['id']): Promise<ApiTokenModel> => this.crudApiService.delete(this.urls.resource(id));
}

export const apiTokenApiService = new ApiTokenApiService();
